import type { ServiceHistory } from '@/types'

export const ELAPSED_THRESHOLD_MS = 5 * 60 * 1000 // 5 minutes

export function formatDuration(ms: number | null | undefined): string {
  if (!ms || ms <= 0) return '--'
  if (ms < 1000) return `${Math.round(ms)}ms`
  const seconds = ms / 1000
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.round(seconds % 60)
  return `${minutes}m ${remainingSeconds}s`
}

export function formatRunTime(startedAt: Date | string): string {
  const start = new Date(startedAt)
  const elapsed = Date.now() - start.getTime()
  if (elapsed < ELAPSED_THRESHOLD_MS) {
    const seconds = Math.floor(elapsed / 1000)
    if (seconds < 60) return `${seconds}s ago`
    return `${Math.floor(seconds / 60)}m ago`
  }
  return start.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  })
}

/**
 * Long-form absolute timestamp — used in run summaries where context (minutes-ago)
 * isn't appropriate.
 */
export function formatRunTimeAbsolute(dateString: string | Date): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

export function getRunStatusBadgeClass(status: string | undefined): string {
  switch (status) {
    case 'Succeeded': return 'bg-success'
    case 'Failed':    return 'bg-danger'
    case 'Running':   return 'bg-primary'
    case 'Warning':   return 'bg-warning'
    // Pending presents as "Running" (see getRunStatusLabel), so it shares the blue.
    case 'Pending':   return 'bg-primary'
    case 'Queued':    return 'bg-secondary'
    default:          return 'bg-secondary'
  }
}

export function getRunStatusLabel(status: string | undefined): string {
  switch (status) {
    case 'Succeeded': return 'Success'
    case 'Failed':    return 'Failed'
    case 'Running':   return 'Running'
    case 'Warning':   return 'Warning'
    case 'Pending':   return 'Running'
    case 'Queued':    return 'Queued'
    default:          return status ?? 'Unknown'
  }
}

/** One count pill for {@link StatusCountChips} — a status, its tally, and display hints.
 *  Color is derived from `status` via {@link getRunStatusBadgeClass} so chips never drift
 *  from status badges elsewhere. */
export interface CountChip {
  status: 'Succeeded' | 'Failed' | 'Warning' | 'Pending'
  count: number
  label?: string
  icon?: string
}

const COUNT_CHIP_ICON: Record<CountChip['status'], string> = {
  Succeeded: 'bi-check-circle',
  Warning: 'bi-exclamation-triangle',
  Failed: 'bi-x-circle',
  Pending: 'bi-clock',
}

/** Build a {@link CountChip} with the canonical icon and a "N <verb>" label. Keeps the
 *  service-card (transaction tallies) and run-header (service rollup) chip rows consistent. */
export function statusCountChip(status: CountChip['status'], count: number, label: string): CountChip {
  return { status, count, label, icon: COUNT_CHIP_ICON[status] }
}

/** A service whose outcome is recorded has finished counting; anything else is still adding to its
 *  numbers. Said as "not finished" rather than "running" because the engine has no Running status — a
 *  service in flight reports Pending, the same as one that has not started, whose counts do not move. */
export function isStillCounting(run: ServiceHistory): boolean {
  return run.status !== 'Succeeded' && run.status !== 'Warning' && run.status !== 'Failed'
}

/** A service's transaction tallies in a fixed order so colors read consistently. Pending is what the
 *  service still holds: sent and not yet settled either way. */
export function serviceCountChips(run: ServiceHistory): CountChip[] {
  const pending = run.total - (run.succeeded + run.warning + run.failed + run.neverResponded)
  return [
    statusCountChip('Succeeded', run.succeeded, `${run.succeeded} succeeded`),
    statusCountChip('Warning', run.warning, `${run.warning} warning`),
    statusCountChip('Failed', run.failed, `${run.failed} failed`),
    statusCountChip('Failed', run.neverResponded, `${run.neverResponded} never answered`),
    statusCountChip('Pending', pending, `${pending} pending`),
  ]
}

/** A finished service that handled no transactions gets an explicit "0", because the count chips hide
 *  zero tallies and would otherwise show nothing. A service still counting has an empty row that climbs. */
export function finishedWithNoTransactions(run: ServiceHistory): boolean {
  return !isStillCounting(run) && run.total === 0
}

// Validation outcome (Phase 3) — Pass / Fail / Drift against a promoted baseline.
export function getValidationBadgeClass(outcome: string | undefined): string {
  switch (outcome) {
    case 'Pass':  return 'bg-success'
    case 'Fail':  return 'bg-danger'
    case 'Drift': return 'bg-warning'
    default:      return 'bg-secondary'
  }
}

export function getValidationLabel(outcome: string | undefined): string {
  switch (outcome) {
    case 'Pass':  return 'Baseline: Pass'
    case 'Fail':  return 'Baseline: Fail'
    case 'Drift': return 'Baseline: Drift'
    default:      return 'Not validated'
  }
}

// Per-service diff kind (ServiceDiff.kind) — how a single service compared against its baseline.
export function getDiffKindBadgeClass(kind: string | undefined): string {
  switch (kind) {
    case 'Match':      return 'bg-success'
    case 'Regressed':  return 'bg-danger'
    case 'CountDrift': return 'bg-warning'
    case 'Missing':    return 'bg-danger'
    case 'Added':      return 'bg-info'
    default:           return 'bg-secondary'
  }
}

export function getDiffKindLabel(kind: string | undefined): string {
  switch (kind) {
    case 'Match':      return 'Match'
    case 'Regressed':  return 'Regressed'
    case 'CountDrift': return 'Count drift'
    case 'Missing':    return 'Missing'
    case 'Added':      return 'Added'
    default:           return kind ?? 'Unknown'
  }
}
